import React from "react";
import { ShieldCheck, Zap, Globe2, Layers, BadgeDollarSign, TrendingUp } from "lucide-react";

const FOREX_IMG =
    "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&w=1400";

const BENEFITS = [
    {
        icon: ShieldCheck,
        title: "Top-Tier Regulation",
        copy: "Licensed by ASIC (Australia), FCA (UK), VFSC, and CIMA — with segregated client funds at AA-rated banks.",
    },
    {
        icon: Zap,
        title: "Lightning Execution",
        copy: "Equinix-hosted infrastructure delivers average order execution under 100ms with deep institutional liquidity.",
    },
    {
        icon: BadgeDollarSign,
        title: "RAW ECN Spreads from 0.0",
        copy: "Direct interbank pricing on the RAW account. Transparent commission, no markup, no requotes.",
    },
    {
        icon: Layers,
        title: "1,000+ Instruments",
        copy: "Forex, indices, commodities, shares, ETFs, bonds, and crypto CFDs — all from a single account.",
    },
    {
        icon: Globe2,
        title: "Global Platforms",
        copy: "MetaTrader 4, MetaTrader 5, and Vantage's proprietary ProTrader — desktop, web, and mobile.",
    },
    {
        icon: TrendingUp,
        title: "Up to 1:500 Leverage",
        copy: "Flexible leverage tiers with negative balance protection. Trade capital-efficiently and safely.",
    },
];

export default function VantageBenefits() {
    return (
        <section id="vantage" className="section relative" data-testid="vantage-section">
            <div className="container-x">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#C8A064]" />
                            <span className="overline">Why Vantage Markets</span>
                        </div>
                        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#F3F4F6] leading-tight">
                            A broker built on{" "}
                            <em className="text-[#C8A064] not-italic">precision, regulation,</em>{" "}
                            and reach.
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-[#94A3B8] leading-relaxed">
                            We chose to partner with Vantage Markets because their offering stands
                            on its own merits. Here are the differentiators we walk every client
                            through.
                        </p>
                    </div>
                </div>

                {/* Image banner */}
                <div className="relative mb-20 overflow-hidden">
                    <img
                        src={FOREX_IMG}
                        alt="Forex platform"
                        className="w-full h-[420px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1547] via-[#0A1547]/40 to-transparent" />
                    <div className="absolute inset-0 flex items-center">
                        <div className="container-x">
                            <div className="max-w-xl">
                                <div className="overline mb-3">Institutional-Grade Infrastructure</div>
                                <div className="font-serif-display text-2xl sm:text-3xl text-[#F3F4F6] leading-snug">
                                    Trade alongside hedge funds, prop desks, and algorithmic firms
                                    on the same liquidity rails.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Benefit grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {BENEFITS.map((b, i) => (
                        <BenefitCard key={i} {...b} index={i} />
                    ))}
                </div>

                {/* Disclaimer */}
                <div className="mt-16 text-xs text-[#64748B] max-w-3xl leading-relaxed">
                    Trading derivatives carries risk and may not be suitable for all investors. Specific
                    products, leverage, and protections vary by jurisdiction and Vantage entity.
                    NexaReach LLC acts as an introducer and is not a regulated financial advisor.
                </div>
            </div>
        </section>
    );
}

const BenefitCard = ({ icon: Icon, title, copy, index }) => (
    <div className="card-solid card-hover p-8" data-testid={`benefit-card-${index}`}>
        <div className="w-12 h-12 border border-[#C8A064]/40 flex items-center justify-center text-[#C8A064] mb-6">
            <Icon size={20} strokeWidth={1.6} />
        </div>
        <h3 className="font-serif-display text-xl text-[#F3F4F6] mb-3 leading-snug">{title}</h3>
        <p className="text-sm text-[#94A3B8] leading-relaxed">{copy}</p>
    </div>
);
